import React from "react";
import cx from "classnames";
import styled from "styled-components";
import { Button, Copy } from "components/components/atoms";
import breakpoints from "utils/breakpoints";
import getEmbedUrl from "utils/getEmbedUrl";
import useBreakpoint from "utils/hooks/useBreakpoint";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import { AspectRatio } from "react-aspect-ratio";
import { videoTranscriptFileUrl } from "utils/videoTranscriptFileUrl";
import { isValidUrl } from "utils/validateUrlSrc";
import { isValidTitle } from "utils/validateTitle";

const SourceContainer = styled.div`
  @media ${breakpoints.lg} {
    width: 55%;
    max-width: 37.5rem;
  }
`;

const SectionHeading = styled.h2`
  letter-spacing: -0.01em;
`;

const SmallTitleTextVideo = ({ fields }) => {
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);

  // Get common fields
  const title = getFieldValue(fields, "title");
  const text = getFieldValue(fields, "text");
  
  // Get the CTA link field 
  const ctaLink = getFieldValue(fields, "ctaLink") || {};
  
  // Retrieve the alternate text field separately from Sitecore.
  const alternateText = ctaLink.alternateText || "";
  
  const cta = {
    text: getFieldValue(fields, "ctaText"),
    href: ctaLink ? ctaLink.href : "",
    target: ctaLink ? ctaLink.target : "",
  };

  const alignment = getFieldValue(fields, "positionMedia");
  const image = getFieldValue(fields, "image") || {};
  const video = getFieldValue(fields, "video") || {};
  const videoDescription = getFieldValue(fields, "videoAltText");
  const videoTranscript = getFieldValue(fields, "videoAltTranscriptFile") || {};
  const autoPlay = getFieldValue(fields, "autoPlay") || false;

  const isRightAligned = alignment === "right";
  const ratio =
    video && video.type
      ? isMobile
        ? "330/200"
        : "640/400"
      : isMobile
      ? "330/300"
      : "640/500";

  return (
    <div
      className={cx("flex flex-col", {
        "md:flex-row": !isRightAligned,
        "md:flex-row-reverse": isRightAligned,
      })}
    >
      <SourceContainer className="w-full overflow-auto rounded mb-10 md:mb-0">
        <AspectRatio ratio={ratio}>
          <div
            className="h-full w-full justify-center text-center"
            key={video ? video.id : image.src}
            isMobile={isMobile}
          >
            {video && video.type ? (
              video.type === "external" ? (
                <iframe
                  className="min-h-full min-w-full bg-white rounded"
                  src={getEmbedUrl(video.href, autoPlay)}
                  title={videoDescription}
                />
              ) : (
                isValidUrl(video.href) && (
                  <video
                    className="min-h-full min-w-full bg-white rounded"
                    src={video.href}
                    controls
                    disablepictureinpicture="true"
                    controlslist="nodownload"
                    autoPlay={autoPlay}
                    muted
                    loop
                  />
                )
              )
            ) : (
              isValidUrl(image.src) && (
                <img
                  src={image.src}
                  alt={image.alt}
                  className="min-h-full min-w-full object-cover"
                />
              )
            )}
          </div>
        </AspectRatio>
        {video &&
          video.type &&
          videoDescription &&
          videoTranscript &&
          videoTranscript.link &&
          videoTranscript.link.href && (
            <span className="richtext text-left text-md leading-7 font-graphie text-mattamy-blue mt-2">
              {"Transcript: "}
              <a
                href={videoTranscriptFileUrl(videoTranscript)}
                download={
                  videoTranscript.name + "." + videoTranscript.extension
                }
                className="underline"
                aria-label={"Download " + videoDescription + " transcript"}
              >
                {videoDescription}
              </a>
            </span>
          )}
      </SourceContainer>
      <div
        className={cx("w-full md:w-1/2", {
          "md:pr-10": isRightAligned,
          "md:pl-10": !isRightAligned,
        })}
      >
        {isValidTitle(title) && (
          <SectionHeading className="pt-2 font-bold font-trade-gothic-20 text-4xl mb-3 text-mattamy-blue leading-tiny">
            {title}
          </SectionHeading>
        )}

        <Copy color="blue" className="pb-2 md:pb-4 leading-relaxed">
          {text}
        </Copy>
        <div className="w-full">
          {cta && cta.text && cta.href && (
            <Button
              key={cta.text}
              variant={isMobile ? "filled" : "outlined"}
              color={isMobile ? "action-blue" : "mattamy-blue"}
              textColor="white"
              className="mb-4 mr-4 px-16 md:px-8 tracking-wider"
              href={cta.href}
              target={cta.target}
              linkWrapperDescription={cta.text}
              aria-label={
                alternateText
                  ? `${alternateText}`
                  : cta.text
              }
            >
              {cta.text}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmallTitleTextVideo;
